import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaChevronDown, FaChevronUp, FaQuestionCircle } from 'react-icons/fa';
import StripeNavbar from './StripeNavbar';

export default function TeacherFAQ() {
  const navigate = useNavigate();
  const [lang, setLang] = useState(() => {
    const cookieLang = document.cookie.match(/taealam_language=([^;]+)/);
    return cookieLang ? cookieLang[1] : 'en';
  });
  const [expandedFaqs, setExpandedFaqs] = useState({});
  const isArabic = lang === 'ar';
  const isMobile = window.innerWidth <= 768;

  const faqs = {
    en: [
      {
        id: 1,
        question: 'How do I create my teacher profile?',
        answer: 'After signing up, you can create your teacher profile by going to your dashboard. Fill in your qualifications, subjects you teach, grade levels, hourly rates, and upload a profile photo. Make sure to complete all required fields to increase your visibility to students.'
      },
      {
        id: 2,
        question: 'How do I set my availability?',
        answer: 'You can set your availability through the Calendar section in your dashboard. You can mark specific days and times when you\'re available to teach. Students will only be able to book sessions during your available hours.'
      },
      {
        id: 3,
        question: 'How do I get paid?',
        answer: 'Payments are processed securely through our platform. After completing a session, payment is automatically transferred to your account. You can view your earnings and request payouts from the Payouts section in your dashboard.'
      },
      {
        id: 4,
        question: 'How much can I charge per hour?',
        answer: 'You set your own hourly rate for each subject you teach. You can adjust your rates at any time from your profile settings. Consider your qualifications, experience, and market rates when setting your prices.'
      },
      {
        id: 5,
        question: 'Can I cancel or reschedule a session?',
        answer: 'Yes, you can cancel or reschedule sessions through your dashboard. However, please check our cancellation policy as there may be restrictions on last-minute cancellations. We recommend communicating with students as early as possible if you need to reschedule.'
      },
      {
        id: 6,
        question: 'How do I communicate with my students?',
        answer: 'Taealam has a built-in messaging system that allows you to chat directly with your students. You can send messages, answer questions, and coordinate session details all within the platform.'
      },
      {
        id: 7,
        question: 'Are sessions automatically recorded?',
        answer: 'Yes, all sessions are automatically recorded and transcribed. Both you and your students can access recordings and transcripts from your dashboard to review the material at any time.'
      },
      {
        id: 8,
        question: 'How do I get reviews from students?',
        answer: 'After completing a session, students are prompted to rate and review their experience with you. These reviews appear on your profile and help other students make informed decisions when choosing a teacher.'
      },
      {
        id: 9,
        question: 'What subjects can I teach?',
        answer: 'You can teach any subject you\'re qualified in. When creating your profile, you can select from a wide range of subjects including Mathematics, Science, Languages, History, and many more. You can add or remove subjects at any time.'
      },
      {
        id: 10,
        question: 'How do I handle technical issues during a session?',
        answer: 'If you encounter technical issues during a session, you can contact our support team immediately through the Help Center. We also recommend testing your equipment (camera, microphone, internet connection) before each session to minimize technical problems.'
      },
      {
        id: 11,
        question: 'Can I teach multiple students at once?',
        answer: 'Currently, Taealam focuses on one-on-one sessions. However, you can schedule multiple individual sessions with different students. Group sessions may be available in future updates.'
      },
      {
        id: 12,
        question: 'How do I improve my profile visibility?',
        answer: 'To improve your profile visibility, make sure your profile is complete with all information, upload a professional photo, set competitive rates, maintain good ratings, and respond promptly to student inquiries. Completing your profile verification also helps.'
      }
    ],
    ar: [
      {
        id: 1,
        question: 'كيف أنشئ ملفي الشخصي كمعلم؟',
        answer: 'بعد التسجيل، يمكنك إنشاء ملفك الشخصي كمعلم من خلال الانتقال إلى لوحة التحكم الخاصة بك. املأ مؤهلاتك والمواد التي تدرسها والمستويات الدراسية والأسعار بالساعة وقم برفع صورة ملف شخصي. تأكد من إكمال جميع الحقول المطلوبة لزيادة ظهورك للطلاب.'
      },
      {
        id: 2,
        question: 'كيف أحدد تواجدي؟',
        answer: 'يمكنك تحديد تواجدك من خلال قسم التقويم في لوحة التحكم الخاصة بك. يمكنك تحديد أيام وأوقات محددة عندما تكون متاحًا للتدريس. سيتمكن الطلاب من حجز الجلسات فقط خلال ساعات تواجدك.'
      },
      {
        id: 3,
        question: 'كيف أحصل على أموالي؟',
        answer: 'يتم معالجة المدفوعات بأمان من خلال منصتنا. بعد إكمال جلسة، يتم تحويل الدفع تلقائيًا إلى حسابك. يمكنك عرض أرباحك وطلب السحب من قسم المدفوعات في لوحة التحكم الخاصة بك.'
      },
      {
        id: 4,
        question: 'كم يمكنني أن أتقاضى في الساعة؟',
        answer: 'تحدد سعرك بالساعة لكل مادة تدرسها. يمكنك تعديل أسعارك في أي وقت من إعدادات ملفك الشخصي. ضع في اعتبارك مؤهلاتك وخبرتك وأسعار السوق عند تحديد أسعارك.'
      },
      {
        id: 5,
        question: 'هل يمكنني إلغاء أو إعادة جدولة جلسة؟',
        answer: 'نعم، يمكنك إلغاء أو إعادة جدولة الجلسات من خلال لوحة التحكم الخاصة بك. ومع ذلك، يرجى التحقق من سياسة الإلغاء الخاصة بنا حيث قد تكون هناك قيود على الإلغاء في اللحظة الأخيرة. نوصي بالتواصل مع الطلاب في أقرب وقت ممكن إذا كنت بحاجة إلى إعادة الجدولة.'
      },
      {
        id: 6,
        question: 'كيف أتواصل مع طلابي؟',
        answer: 'تعلّم لديه نظام مراسلة مدمج يتيح لك الدردشة مباشرة مع طلابك. يمكنك إرسال الرسائل والإجابة على الأسئلة وتنسيق تفاصيل الجلسة كل ذلك داخل المنصة.'
      },
      {
        id: 7,
        question: 'هل يتم تسجيل الجلسات تلقائيًا؟',
        answer: 'نعم، يتم تسجيل جميع الجلسات ونسخها تلقائيًا. يمكنك أنت وطلابك الوصول إلى التسجيلات والنصوص من لوحة التحكم الخاصة بك لمراجعة المادة في أي وقت.'
      },
      {
        id: 8,
        question: 'كيف أحصل على تقييمات من الطلاب؟',
        answer: 'بعد إكمال جلسة، يُطلب من الطلاب تقييم ومراجعة تجربتهم معك. تظهر هذه المراجعات على ملفك الشخصي وتساعد الطلاب الآخرين على اتخاذ قرارات مستنيرة عند اختيار معلم.'
      },
      {
        id: 9,
        question: 'ما هي المواد التي يمكنني تدريسها؟',
        answer: 'يمكنك تدريس أي مادة أنت مؤهل فيها. عند إنشاء ملفك الشخصي، يمكنك الاختيار من مجموعة واسعة من المواد بما في ذلك الرياضيات والعلوم واللغات والتاريخ والعديد غيرها. يمكنك إضافة أو إزالة المواد في أي وقت.'
      },
      {
        id: 10,
        question: 'كيف أتعامل مع المشاكل التقنية أثناء الجلسة؟',
        answer: 'إذا واجهت مشاكل تقنية أثناء الجلسة، يمكنك الاتصال بفريق الدعم لدينا على الفور من خلال مركز المساعدة. نوصي أيضًا باختبار معداتك (الكاميرا والميكروفون واتصال الإنترنت) قبل كل جلسة لتقليل المشاكل التقنية.'
      },
      {
        id: 11,
        question: 'هل يمكنني تدريس عدة طلاب في وقت واحد؟',
        answer: 'حاليًا، يركز تعلّم على الجلسات الفردية. ومع ذلك، يمكنك جدولة عدة جلسات فردية مع طلاب مختلفين. قد تكون الجلسات الجماعية متاحة في التحديثات المستقبلية.'
      },
      {
        id: 12,
        question: 'كيف أحسن ظهور ملفي الشخصي؟',
        answer: 'لتحسين ظهور ملفك الشخصي، تأكد من اكتمال ملفك الشخصي بجميع المعلومات، وارفع صورة احترافية، وحدد أسعارًا تنافسية، وحافظ على تقييمات جيدة، واستجب بسرعة لاستفسارات الطلاب. يساعد إكمال التحقق من ملفك الشخصي أيضًا.'
      }
    ]
  };

  const toggleFaq = (id) => {
    setExpandedFaqs(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  return (
    <div dir={isArabic ? 'rtl' : 'ltr'} style={{ minHeight: '100vh', background: '#F8FAFC' }}>
      <StripeNavbar lang={lang} setLang={setLang} alwaysScrolled={true} />
      
      <div style={{
        maxWidth: 900,
        margin: '0 auto',
        padding: isMobile ? '100px 20px 60px' : '120px 40px 80px'
      }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 60 }}>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #FF7426 0%, #FF9854 100%)',
            marginBottom: 24,
            color: 'white'
          }}>
            <FaQuestionCircle size={32} />
          </div>
          <h1 style={{
            fontSize: isMobile ? 32 : 42,
            fontWeight: 800,
            color: '#0F172A',
            marginBottom: 12,
            fontFamily: "'IBM Plex Sans Arabic', sans-serif"
          }}>
            {isArabic ? 'الأسئلة الشائعة للمعلمين' : 'Frequently Asked Questions for Teachers'}
          </h1>
          <p style={{
            fontSize: 18,
            color: '#64748B',
            maxWidth: 600,
            margin: '0 auto',
            lineHeight: 1.6,
            fontFamily: "'IBM Plex Sans Arabic', sans-serif"
          }}>
            {isArabic 
              ? 'ابحث عن إجابات لأسئلتك الشائعة حول التدريس على منصة تعلّم'
              : 'Find answers to common questions about teaching on Taealam platform'}
          </p>
        </div>

        {/* FAQ List */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {faqs[lang].map((faq) => (
            <div
              key={faq.id}
              style={{
                background: '#FFFFFF',
                borderRadius: 16,
                border: '1px solid #E2E8F0',
                overflow: 'hidden',
                transition: 'all 0.3s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.08)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <button
                onClick={() => toggleFaq(faq.id)}
                style={{
                  width: '100%',
                  padding: '20px 24px',
                  background: 'transparent',
                  border: 'none',
                  textAlign: 'left',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 16
                }}
              >
                <h3 style={{
                  fontSize: 18,
                  fontWeight: 600,
                  color: '#0F172A',
                  margin: 0,
                  flex: 1,
                  fontFamily: "'IBM Plex Sans Arabic', sans-serif",
                  ...(isArabic ? { textAlign: 'right' } : {})
                }}>
                  {faq.question}
                </h3>
                <div style={{
                  color: '#FF7426',
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  {expandedFaqs[faq.id] ? <FaChevronUp size={20} /> : <FaChevronDown size={20} />}
                </div>
              </button>
              
              {expandedFaqs[faq.id] && (
                <div style={{
                  padding: '0 24px 20px 24px',
                  borderTop: '1px solid #E2E8F0'
                }}>
                  <p style={{
                    fontSize: 16,
                    color: '#64748B',
                    lineHeight: 1.7,
                    margin: '16px 0 0 0',
                    fontFamily: "'IBM Plex Sans Arabic', sans-serif"
                  }}>
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Help Section */}
        <div style={{
          marginTop: 60,
          padding: '40px',
          background: 'linear-gradient(135deg, #FF7426 0%, #FF9854 100%)',
          borderRadius: 20,
          textAlign: 'center',
          color: 'white'
        }}>
          <h2 style={{
            fontSize: 24,
            fontWeight: 700,
            marginBottom: 12,
            fontFamily: "'IBM Plex Sans Arabic', sans-serif"
          }}>
            {isArabic ? 'لا تزال لديك أسئلة؟' : 'Still have questions?'}
          </h2>
          <p style={{
            fontSize: 16,
            marginBottom: 24,
            opacity: 0.95,
            fontFamily: "'IBM Plex Sans Arabic', sans-serif"
          }}>
            {isArabic 
              ? 'قم بزيارة مركز المساعدة للحصول على المزيد من المعلومات'
              : 'Visit our Help Center for more information'}
          </p>
          <button
            onClick={() => navigate('/help-center')}
            style={{
              background: 'white',
              color: '#FF7426',
              border: 'none',
              padding: '12px 32px',
              borderRadius: 50,
              fontSize: 16,
              fontWeight: 600,
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              fontFamily: "'IBM Plex Sans Arabic', sans-serif"
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            {isArabic ? 'زيارة مركز المساعدة' : 'Visit Help Center'}
          </button>
        </div>
      </div>
    </div>
  );
}

